import React, { useCallback, useState } from "react";
import { Mic } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useVoiceAssistant } from "../../lib/voice/useVoiceAssistant";
import { useUserPref } from "../../lib/useUserPref";
import VoiceCommandsGuide from "../guide/VoiceCommandsGuide";

/**
 * OraTimeButton — pulsante microfono discreto per l'assistente vocale
 * del TIME Watch.
 *
 * Comportamento:
 *  - Tap sul microfono → apre il popup dei Comandi Vocali (VoiceCommandsGuide)
 *    solo se l'utente NON ha selezionato "Non mostrare più".
 *  - Il popup contiene esempi curati dei comandi principali e la checkbox
 *    "Non mostrare più automaticamente" (preferenza `voiceCommandsDismissed`,
 *    persistita per-utente via `useUserPref`).
 *  - Alla chiusura del popup parte automaticamente l'ascolto vocale.
 *  - Se la preferenza è già dismissed → tap microfono avvia direttamente
 *    l'ascolto. Il popup resta comunque riapribile dal Centro Guida.
 *  - Feedback dell'ascolto (small toast) e comando "aiuto" restano invariati.
 *
 * Il Libretto di Benvenuto non viene toccato: è gestito dal Centro Guida
 * separatamente.
 */
export default function OraTimeButton({ className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Preferenza per l'auto-apertura del popup Comandi Vocali.
  // Default: false → il popup si mostra al primo tap.
  const [voiceGuideDismissed, setVoiceGuideDismissed] = useUserPref(
    "voiceCommandsDismissed",
    false
  );
  const [showVoiceGuide, setShowVoiceGuide] = useState(false);

  const handleCommand = useCallback(
    (cmd) => {
      if (!cmd) return;
      if (cmd.action === "help") {
        // "aiuto" pronunciato → riapre il popup Comandi Vocali anche se
        // l'utente aveva scelto "non mostrare più".
        setShowVoiceGuide(true);
        return;
      }
      if (cmd.action === "navigate") {
        navigate(cmd.target);
        return;
      }
      if (cmd.action === "pmv-page") {
        const pmvPath = "/innovazione/time-watch/pmv";
        const hash = `#page-${cmd.target}`;
        if (location.pathname !== pmvPath) {
          navigate(`${pmvPath}${hash}`);
        } else if (window.location.hash === hash) {
          window.dispatchEvent(
            new CustomEvent("time-watch:pmv-go", {
              detail: { page: cmd.target },
            })
          );
        } else {
          window.location.hash = hash;
        }
      }
    },
    [navigate, location.pathname]
  );

  const { supported, listening, status, feedback, start } = useVoiceAssistant({
    onCommand: handleCommand,
  });

  // Tap microfono:
  //  - se il popup Comandi Vocali NON è stato dismissed → mostralo, poi
  //    all'"Ho capito" farà partire l'ascolto (via handleGuideClose).
  //  - se già dismissed → avvia direttamente l'ascolto.
  const handleMicPress = useCallback(() => {
    if (!voiceGuideDismissed) {
      setShowVoiceGuide(true);
      return;
    }
    start();
  }, [voiceGuideDismissed, start]);

  const handleGuideClose = useCallback(
    (opts = {}) => {
      setShowVoiceGuide(false);
      // Se l'utente parte dal tap del microfono, dopo la chiusura del popup
      // avviamo comunque l'ascolto — così il popup non è un ostacolo.
      if (opts.autoStart !== false) {
        // Piccolo delay per lasciare terminare la transizione UI.
        setTimeout(() => {
          try {
            start();
          } catch {
            /* ignore */
          }
        }, 50);
      }
    },
    [start]
  );

  const handleDontShowAgain = useCallback(
    (value) => {
      setVoiceGuideDismissed(!!value);
    },
    [setVoiceGuideDismissed]
  );

  const disabled = !supported;

  return (
    <>
      <div
        data-testid="ora-time-voice"
        className={`fixed bottom-[168px] sm:bottom-24 right-4 sm:right-6 z-50 flex flex-col items-end gap-2 pointer-events-none ${className}`}
      >
        {feedback && (
          <div
            data-testid="ora-time-feedback"
            className={`pointer-events-auto max-w-[70vw] sm:max-w-xs px-3 py-1.5 rounded-full text-[10px] tracking-[0.22em] uppercase font-mono backdrop-blur-md bg-black/60 border transition-colors ${
              status === "success"
                ? "border-emerald-400/45 text-emerald-300"
                : status === "listening"
                ? "border-emerald-400/30 text-emerald-200/90"
                : "border-white/15 text-white/70"
            }`}
          >
            {feedback}
          </div>
        )}

        <button
          type="button"
          onClick={handleMicPress}
          disabled={disabled}
          data-testid="ora-time-mic-button"
          aria-label={
            listening
              ? "Sto ascoltando. Pronuncia Ora TIME e il comando."
              : "Attiva assistente vocale Ora TIME"
          }
          aria-pressed={listening}
          className={`pointer-events-auto relative w-11 h-11 rounded-full backdrop-blur-md bg-black/50 border transition-all duration-300 flex items-center justify-center ${
            listening
              ? "border-emerald-400/70 text-emerald-300 shadow-[0_0_24px_rgba(16,185,129,0.35)]"
              : "border-white/15 text-white/70 hover:text-white hover:border-white/40"
          } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
        >
          {listening && (
            <>
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full border border-emerald-400/50 animate-ping"
              />
              <span
                aria-hidden="true"
                className="absolute -inset-1 rounded-full border border-emerald-400/20"
              />
            </>
          )}
          <Mic className="w-4 h-4 relative z-10" />
        </button>
      </div>

      <VoiceCommandsGuide
        open={showVoiceGuide}
        initialDontShow={voiceGuideDismissed}
        onClose={handleGuideClose}
        onDontShowAgain={handleDontShowAgain}
      />
    </>
  );
}
